import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'react-hot-toast';
import './AIMetricsDashboard.css';

const DEFAULT_FILTERS = {
  startDate: '',
  endDate: '',
  search: '',
  page: 1,
  limit: 25,
  sortBy: 'totalTokens',
  sortDir: 'desc'
};

const SUMMARY_CARDS = [
  { key: 'requestCount', label: 'AI Requests', icon: 'fa-comments' },
  { key: 'totalTokens', label: 'Total Tokens', icon: 'fa-coins' },
  { key: 'uniqueUsers', label: 'Users With Usage', icon: 'fa-users' },
  { key: 'avgTokensPerRequest', label: 'Avg Tokens / Request', icon: 'fa-chart-line' }
];

const TABLE_COLUMNS = [
  { key: 'name', label: 'User', sortable: true },
  { key: 'subscriptionTier', label: 'Tier', sortable: true },
  { key: 'requestCount', label: 'Requests', sortable: true },
  { key: 'inputTokens', label: 'Input Tokens', sortable: true },
  { key: 'outputTokens', label: 'Output Tokens', sortable: true },
  { key: 'totalTokens', label: 'Total Tokens', sortable: true },
  { key: 'avgTokensPerRequest', label: 'Avg / Request', sortable: true },
  { key: 'topFeature', label: 'Top Feature', sortable: true },
  { key: 'lastUsedAt', label: 'Last Used', sortable: true }
];

const formatNumber = (value) => new Intl.NumberFormat('en-US').format(Number(value) || 0);

const formatDateTime = (value) => {
  if (!value) return '-';
  return new Date(value).toLocaleString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

const prettifyFeature = (value) =>
  String(value || 'unknown')
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());

const AIMetricsDashboard = () => {
  const [stats, setStats] = useState(null);
  const [filters, setFilters] = useState(DEFAULT_FILTERS);
  const [startDateInput, setStartDateInput] = useState('');
  const [endDateInput, setEndDateInput] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const queryString = useMemo(() => {
    const params = new URLSearchParams();
    if (filters.startDate) params.set('startDate', filters.startDate);
    if (filters.endDate) params.set('endDate', filters.endDate);
    if (filters.search) params.set('search', filters.search);
    params.set('page', String(filters.page));
    params.set('limit', String(filters.limit));
    params.set('sortBy', filters.sortBy);
    params.set('sortDir', filters.sortDir);
    return params.toString();
  }, [filters]);

  const fetchAIMetrics = useCallback(async () => {
    try {
      setLoading(true);
      setError('');

      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_API_URL}/admin/ai-token-stats?${queryString}`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch AI token statistics');
      }

      const data = await response.json();
      setStats(data);
    } catch (err) {
      console.error('Error fetching AI metrics:', err);
      setError(err.message);
      toast.error('Failed to load AI metrics');
    } finally {
      setLoading(false);
    }
  }, [queryString]);

  useEffect(() => {
    fetchAIMetrics();
  }, [fetchAIMetrics]);

  const handleApplyFilters = () => {
    setFilters((prev) => ({
      ...prev,
      startDate: startDateInput,
      endDate: endDateInput,
      search: searchInput.trim(),
      page: 1
    }));
  };

  const handleClearFilters = () => {
    setStartDateInput('');
    setEndDateInput('');
    setSearchInput('');
    setFilters((prev) => ({
      ...prev,
      startDate: '',
      endDate: '',
      search: '',
      page: 1
    }));
  };

  const handleSort = (field) => {
    setFilters((prev) => {
      if (prev.sortBy === field) {
        return {
          ...prev,
          sortDir: prev.sortDir === 'asc' ? 'desc' : 'asc',
          page: 1
        };
      }

      return {
        ...prev,
        sortBy: field,
        sortDir: 'desc',
        page: 1
      };
    });
  };

  const handleLimitChange = (event) => {
    const nextLimit = parseInt(event.target.value, 10);
    setFilters((prev) => ({
      ...prev,
      limit: Number.isNaN(nextLimit) ? 25 : nextLimit,
      page: 1
    }));
  };

  const handlePageChange = (nextPage) => {
    if (!stats?.meta) return;
    if (nextPage < 1 || nextPage > stats.meta.totalPages) return;
    setFilters((prev) => ({ ...prev, page: nextPage }));
  };

  const getSortIndicator = (field) => {
    if (filters.sortBy !== field) return '';
    return filters.sortDir === 'asc' ? ' ^' : ' v';
  };

  if (loading && !stats) {
    return (
      <div className="ai-metrics loading-state">
        <div className="loading-spinner"></div>
        <p>Loading AI metrics...</p>
      </div>
    );
  }

  if (error && !stats) {
    return (
      <div className="ai-metrics error-state">
        <p>{error}</p>
        <button onClick={fetchAIMetrics} className="retry-button" type="button">
          <i className="fas fa-redo"></i> Retry
        </button>
      </div>
    );
  }

  if (!stats) {
    return null;
  }

  const { summary, featureBreakdown, trend, perUserStats, meta } = stats;
  const trendMax = Math.max(...trend.map((item) => item.totalTokens || 0), 1);
  const canGoPrev = meta.page > 1;
  const canGoNext = meta.page < meta.totalPages;

  return (
    <div className="ai-metrics">
      <div className="ai-metrics-header">
        <div>
          <h2>
            <i className="fas fa-microchip"></i> AI Token Metrics
          </h2>
          <p className="stats-subtitle">
            Monitor token usage across AI Coach, recipe generation, meal planning, and import/extraction features.
          </p>
          {meta.hasDateFilter && (
            <p className="filter-scope">
              Date range: {formatDateTime(meta.startDate)} to {formatDateTime(meta.endDate)}
            </p>
          )}
        </div>
        <button className="refresh-button" onClick={fetchAIMetrics} disabled={loading} type="button">
          <i className="fas fa-sync-alt"></i> Refresh
        </button>
      </div>

      <div className="ai-metrics-filters">
        <div className="filter-group">
          <label htmlFor="ai-metrics-start-date">Start Date</label>
          <input
            id="ai-metrics-start-date"
            type="date"
            value={startDateInput}
            onChange={(event) => setStartDateInput(event.target.value)}
          />
        </div>

        <div className="filter-group">
          <label htmlFor="ai-metrics-end-date">End Date</label>
          <input
            id="ai-metrics-end-date"
            type="date"
            value={endDateInput}
            onChange={(event) => setEndDateInput(event.target.value)}
          />
        </div>

        <div className="filter-group filter-search">
          <label htmlFor="ai-metrics-user-search">Search User / Feature</label>
          <input
            id="ai-metrics-user-search"
            type="text"
            placeholder="Name, email, user ID, or feature"
            value={searchInput}
            onChange={(event) => setSearchInput(event.target.value)}
          />
        </div>

        <div className="filter-actions">
          <button onClick={handleApplyFilters} className="apply-filter-btn" disabled={loading} type="button">
            Apply
          </button>
          <button onClick={handleClearFilters} className="clear-filter-btn" disabled={loading} type="button">
            Clear
          </button>
        </div>
      </div>

      <div className="stats-summary-grid">
        {SUMMARY_CARDS.map((card) => (
          <div key={card.key} className="stat-card">
            <div className="stat-icon">
              <i className={`fas ${card.icon}`}></i>
            </div>
            <div className="stat-content">
              <h3>{formatNumber(summary[card.key])}</h3>
              <p>{card.label}</p>
            </div>
          </div>
        ))}
      </div>

      <div className="stats-section two-column">
        <div className="stats-subsection">
          <h3 className="section-header">
            <i className="fas fa-layer-group"></i> Feature Breakdown
          </h3>
          <div className="feature-list">
            {featureBreakdown.length === 0 ? (
              <p className="empty-text">No feature usage found for current filters.</p>
            ) : (
              featureBreakdown.map((feature) => (
                <div key={feature.feature} className="feature-row">
                  <div className="feature-main">
                    <span className="feature-name">{prettifyFeature(feature.feature)}</span>
                    <span className="feature-requests">{formatNumber(feature.requestCount)} requests</span>
                  </div>
                  <div className="feature-tokens">{formatNumber(feature.totalTokens)} tokens</div>
                </div>
              ))
            )}
          </div>
        </div>

        <div className="stats-subsection">
          <h3 className="section-header">
            <i className="fas fa-chart-area"></i> Daily Token Trend
          </h3>
          {trend.length === 0 ? (
            <p className="empty-text">No trend data available yet.</p>
          ) : (
            <div className="trend-bars">
              {trend.map((entry) => (
                <div key={entry.date} className="trend-bar-wrapper" title={`${entry.date}: ${formatNumber(entry.totalTokens)} tokens`}>
                  <div className="trend-bar-value">{formatNumber(entry.totalTokens)}</div>
                  <div
                    className="trend-bar"
                    style={{ height: `${Math.max(6, (entry.totalTokens / trendMax) * 120)}px` }}
                  ></div>
                  <div className="trend-bar-date">{entry.date.slice(5)}</div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="stats-section">
        <div className="table-controls">
          <h3 className="section-header">
            <i className="fas fa-table"></i> Per-User Token Usage
          </h3>
          <div className="table-control-group">
            <label htmlFor="ai-metrics-rows">Rows</label>
            <select id="ai-metrics-rows" value={filters.limit} onChange={handleLimitChange}>
              <option value={10}>10</option>
              <option value={25}>25</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
            </select>
          </div>
        </div>

        <div className="stats-table-wrapper">
          {perUserStats.length === 0 ? (
            <div className="empty-state">
              <i className="fas fa-database"></i>
              <p>No user token usage found for current filters.</p>
            </div>
          ) : (
            <table className="stats-table">
              <thead>
                <tr>
                  {TABLE_COLUMNS.map((column) => (
                    <th key={column.key}>
                      {column.sortable ? (
                        <button
                          className="sort-button"
                          onClick={() => handleSort(column.key)}
                          type="button"
                        >
                          {column.label}{getSortIndicator(column.key)}
                        </button>
                      ) : (
                        column.label
                      )}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {perUserStats.map((row) => (
                  <tr key={`${row.userId}:${row.lastUsedAt || 'none'}`}>
                    <td>
                      <div className="user-name">{row.name || 'Unknown User'}</div>
                      <div className="user-meta">{row.email || row.userId}</div>
                    </td>
                    <td>
                      <span className="tier-badge">{row.subscriptionTier || 'unknown'}</span>
                    </td>
                    <td>{formatNumber(row.requestCount)}</td>
                    <td>{formatNumber(row.inputTokens)}</td>
                    <td>{formatNumber(row.outputTokens)}</td>
                    <td>{formatNumber(row.totalTokens)}</td>
                    <td>{formatNumber(row.avgTokensPerRequest)}</td>
                    <td>{prettifyFeature(row.topFeature)}</td>
                    <td>{formatDateTime(row.lastUsedAt)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        <div className="table-pagination">
          <button onClick={() => handlePageChange(meta.page - 1)} disabled={!canGoPrev} type="button">
            Previous
          </button>
          <span>
            Page {meta.page} of {meta.totalPages} | {formatNumber(meta.totalRows)} users
          </span>
          <button onClick={() => handlePageChange(meta.page + 1)} disabled={!canGoNext} type="button">
            Next
          </button>
        </div>
      </div>
    </div>
  );
};

export default AIMetricsDashboard;
